import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { getMappingErrors, supportsCustomFieldMappings } from "./custom-field-mappings";
import { CustomFieldsSelector } from "./custom-fields-selector";
import { toExportCsvRequest } from "./export-csv";
import { FileUploadZone } from "./file-upload-zone";
import { FreshserviceGroupScope } from "./freshservice-group-scope";
import { HarmonyFormatInfo } from "./harmony-format-info";
import { useCreateImportJob, useTenantCredentials, getAvailableProviders, getCredentialsForProvider } from "@/hooks/use-itsm-import";
import { useDeskCustomFields, useDesks, useSubdesks } from "@/hooks/use-desk-migration";
import { useIntegrationSyncLinkedTickets } from "@/hooks/use-itsm-config";
import { IMPORT_PROVIDERS, FILE_FORMAT_PROVIDERS } from "@/lib/types/itsm-import";
import type { ImportProvider, CreateImportJobRequest, CustomFieldMapping } from "@/lib/types/itsm-import";

// Radix Select items cannot use an empty value, so "automatic" gets a sentinel
const AUTO_SUBDESK_VALUE = "__automatic__";

// Sent as record_types. "" means every type, which is the API's default and
// what every import did before this option existed.
const RECORD_TYPE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "change", label: "Changes only" },
  { value: "ticket", label: "Tickets only" },
] as const;
const ALL_RECORD_TYPES_VALUE = "__all__";

// Tri-state override of the integration's sync_linked_tickets setting.
type LinkedTicketsMode = "inherit" | "on" | "off";
const LINKED_TICKETS_OPTIONS: { value: LinkedTicketsMode; label: string }[] = [
  { value: "inherit", label: "Inherit from integration" },
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
];

function toLinkedTicketsMode(value: boolean | null | undefined): LinkedTicketsMode {
  if (value == null) return "inherit";
  return value ? "on" : "off";
}

const formSchema = z
  .object({
    source: z.enum(["provider", "file"]),
    provider: z.string().optional(),
    credential_id: z.string().optional(),
    source_project: z.string().optional(),
    target_desk_id: z.string().optional().default(""),
    target_subdesk_id: z.string().optional().default(""),
    since_date: z.string().optional(),
    until_date: z.string().optional(),
    include_closed: z.boolean().default(true),
    record_types: z.string().default(""),
    custom_field_mappings: z
      .array(
        z.object({
          source_field_id: z.string(),
          source_field_name: z.string().nullable().optional(),
          method: z.enum(["harmony_custom_field", "description"]),
          harmony_field_name: z.string().nullable().optional(),
        }),
      )
      .nullable()
      .default(null),
    file_format: z.string().optional(),
    s3_keys: z.array(z.string()).default([]),
    dry_run: z.boolean().default(false),
    skip_dedup: z.boolean().default(false),
    rate_limit: z.string().optional(),
    max_tickets: z.string().optional(),
    transform_code: z.string().optional(),
    create_tickets: z.boolean().default(true),
    generate_insights: z.boolean().default(false),
    include_audit_log: z.boolean().default(false),
    include_custom_field_history: z.boolean().default(false),
    sync_linked_tickets: z.enum(["inherit", "on", "off"]).default("inherit"),
    export_csv: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (!data.export_csv && data.create_tickets && !data.target_desk_id) {
      ctx.addIssue({
        code: "custom",
        message: "Target desk is required when creating tickets",
        path: ["target_desk_id"],
      });
    }
    if (data.source === "provider") {
      if (!data.provider) {
        ctx.addIssue({ code: "custom", message: "Provider is required", path: ["provider"] });
      }
      if (!data.credential_id) {
        ctx.addIssue({ code: "custom", message: "Credential ID is required", path: ["credential_id"] });
      }
      // A monday import reads one board, and the importer refuses a job that
      // does not name it - or that skips the dedup keeping Harmony's own
      // exported items off the board from coming back as tickets.
      if (data.provider === "monday" && !data.source_project?.trim()) {
        ctx.addIssue({ code: "custom", message: "Board ID is required for monday", path: ["source_project"] });
      }
      if (data.provider === "monday" && data.skip_dedup) {
        ctx.addIssue({ code: "custom", message: "monday imports always deduplicate", path: ["skip_dedup"] });
      }
      if (
        !data.export_csv &&
        sendsCustomFieldMappings(data) &&
        data.custom_field_mappings &&
        getMappingErrors(data.custom_field_mappings).size > 0
      ) {
        ctx.addIssue({
          code: "custom",
          message: "Fix the custom field mappings above",
          path: ["custom_field_mappings"],
        });
      }
    }
    if (data.source === "file") {
      if (!data.file_format) {
        ctx.addIssue({ code: "custom", message: "File format is required", path: ["file_format"] });
      }
      if (data.s3_keys.length === 0) {
        ctx.addIssue({ code: "custom", message: "At least one file must be uploaded", path: ["s3_keys"] });
      }
    }
  });

type FormValues = z.infer<typeof formSchema>;

/** The custom field section applies to JSM provider imports of one project. */
function sendsCustomFieldMappings(values: {
  source: string;
  provider?: string;
  source_project?: string;
}) {
  return (
    values.source === "provider" &&
    supportsCustomFieldMappings(values.provider) &&
    Boolean(values.source_project?.trim())
  );
}

export interface ImportJobFormInitialValues {
  source?: "provider" | "file";
  provider?: string;
  credential_id?: string;
  source_project?: string;
  target_desk_id?: string;
  target_subdesk_id?: string;
  since_date?: string;
  until_date?: string;
  include_closed?: boolean;
  record_types?: string[];
  custom_field_mappings?: CustomFieldMapping[] | null;
  sync_linked_tickets?: boolean | null;
  dry_run?: boolean;
  skip_dedup?: boolean;
  rate_limit?: string;
  max_tickets?: string;
  transform_code?: string;
  export_csv?: boolean;
}

interface ImportJobFormProps {
  tenantId: string;
  onSuccess: (jobId: string) => void;
  initialValues?: ImportJobFormInitialValues;
}


/** A 422 carries FastAPI's list of validation errors rather than a string;
 *  surface the first message instead of rendering the raw list. */
function apiErrorDetail(detail: unknown): string | undefined {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const first: unknown = detail[0];
    if (first && typeof first === "object" && "msg" in first) {
      // Pydantic appends the full error dump after the first line.
      return String(first.msg).split(/\r?\n/)[0];
    }
  }
  return undefined;
}

export function ImportJobForm({ tenantId, onSuccess, initialValues }: ImportJobFormProps) {
  const [advancedOpen, setAdvancedOpen] = useState(Boolean(initialValues?.rate_limit || initialValues?.max_tickets || initialValues?.transform_code || initialValues?.dry_run || initialValues?.skip_dedup));
  const [isUploading, setIsUploading] = useState(false);
  const { data: desks } = useDesks(tenantId);
  const { data: allCredentials, isLoading: credentialsLoading, isError: credentialsError } = useTenantCredentials(tenantId);
  const createJob = useCreateImportJob(tenantId);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema) as never,
    defaultValues: {
      source: initialValues?.source ?? "provider",
      provider: initialValues?.provider,
      file_format: initialValues?.provider,
      credential_id: initialValues?.credential_id,
      source_project: initialValues?.source_project,
      target_desk_id: initialValues?.target_desk_id ?? "",
      target_subdesk_id: initialValues?.target_subdesk_id ?? "",
      since_date: initialValues?.since_date,
      until_date: initialValues?.until_date,
      include_closed: initialValues?.include_closed ?? true,
      record_types: initialValues?.record_types?.[0] ?? "",
      custom_field_mappings: initialValues?.custom_field_mappings ?? null,
      s3_keys: [],
      dry_run: initialValues?.dry_run ?? false,
      skip_dedup: initialValues?.skip_dedup ?? false,
      rate_limit: initialValues?.rate_limit,
      max_tickets: initialValues?.max_tickets,
      transform_code: initialValues?.transform_code,
      create_tickets: true,
      generate_insights: false,
      include_audit_log: false,
      include_custom_field_history: false,
      sync_linked_tickets: toLinkedTicketsMode(initialValues?.sync_linked_tickets),
      export_csv:
        initialValues?.source !== "file" && (initialValues?.export_csv ?? false),
    },
  });

  const source = form.watch("source");
  const s3Keys = form.watch("s3_keys");
  const selectedProvider = form.watch("provider") as ImportProvider | undefined;
  const createTickets = form.watch("create_tickets");
  const includeAuditLog = form.watch("include_audit_log");
  const exportCsv = form.watch("export_csv");
  const showsJiraImportOptions =
    supportsCustomFieldMappings(selectedProvider) && !exportCsv;
  const targetDeskId = form.watch("target_desk_id");
  const { data: subdesks } = useSubdesks(tenantId, targetDeskId);
  const hasSubdesks = Boolean(subdesks?.some((s) => !s.is_default));
  const credentialId = form.watch("credential_id") ?? "";
  const inheritedLinkedTickets = useIntegrationSyncLinkedTickets(
    tenantId,
    supportsCustomFieldMappings(selectedProvider) && credentialId
      ? credentialId
      : undefined,
  );
  const sourceProjectKey = (form.watch("source_project") ?? "").trim();
  const { data: deskCustomFields } = useDeskCustomFields(
    tenantId,
    form.watch("custom_field_mappings") ? targetDeskId : "",
  );

  const availableProviders = allCredentials ? getAvailableProviders(allCredentials) : [];
  const filteredCredentials = selectedProvider && allCredentials
    ? getCredentialsForProvider(allCredentials, selectedProvider)
    : [];

  useEffect(() => {
    form.setValue("credential_id", "");
  }, [selectedProvider, form]);

  const prevDeskIdRef = useRef(targetDeskId);
  useEffect(() => {
    if (prevDeskIdRef.current !== targetDeskId) {
      prevDeskIdRef.current = targetDeskId;
      form.setValue("target_subdesk_id", "");
    }
  }, [targetDeskId, form]);

  // Selected custom fields belong to a project; keep the switch, drop the rows.
  const prevProjectKeyRef = useRef(sourceProjectKey);
  useEffect(() => {
    if (prevProjectKeyRef.current !== sourceProjectKey) {
      prevProjectKeyRef.current = sourceProjectKey;
      if (form.getValues("custom_field_mappings") !== null) {
        form.setValue("custom_field_mappings", []);
      }
    }
  }, [sourceProjectKey, form]);

  const handleUploadComplete = useCallback(
    (s3Key: string) => {
      const current = form.getValues("s3_keys");
      form.setValue("s3_keys", [...current, s3Key], { shouldValidate: true });
    },
    [form],
  );

  const handleRemoveKey = useCallback(
    (key: string) => {
      const current = form.getValues("s3_keys");
      form.setValue(
        "s3_keys",
        current.filter((k) => k !== key),
        { shouldValidate: true },
      );
    },
    [form],
  );

  const onSubmit = async (values: FormValues) => {
    const request: CreateImportJobRequest = {
      source: values.source,
      target_desk_id: values.target_desk_id || undefined,
      target_subdesk_id: values.target_subdesk_id || undefined,
      create_tickets: values.create_tickets,
      generate_insights: values.generate_insights,
      dry_run: values.dry_run,
      skip_dedup: values.skip_dedup,
    };

    if (values.source === "provider") {
      request.provider = values.provider as ImportProvider;
      request.credential_id = values.credential_id;
      request.source_project = values.source_project || undefined;
      request.since_date = values.since_date || undefined;
      request.until_date = values.until_date || undefined;
      request.include_closed = values.include_closed;
      // Freshservice is the only provider that separates changes from
      // tickets; the API rejects the field for anything else.
      if (values.record_types && values.provider === "freshservice") {
        request.record_types = [values.record_types];
      }
      if (supportsCustomFieldMappings(values.provider)) {
        request.include_audit_log = values.include_audit_log;
        if (values.include_audit_log) {
          request.include_custom_field_history =
            values.include_custom_field_history;
        }
        request.custom_field_mappings = sendsCustomFieldMappings(values)
          ? values.custom_field_mappings
          : null;
        // Omitted when inheriting, so the integration's setting applies.
        if (values.sync_linked_tickets !== "inherit") {
          request.sync_linked_tickets = values.sync_linked_tickets === "on";
        }
      }
      if (!values.export_csv && request.custom_field_mappings && deskCustomFields) {
        const deskFieldNames = new Set(deskCustomFields.map((f) => f.name));
        const errors = getMappingErrors(request.custom_field_mappings, deskFieldNames);
        if (errors.size > 0) {
          form.setError("custom_field_mappings", {
            message: "Fix the custom field mappings above",
          });
          return;
        }
      }
    } else {
      request.s3_keys = values.s3_keys;
      if (values.file_format) {
        request.provider = values.file_format as ImportProvider;
      }
    }

    if (values.rate_limit) {
      request.rate_limit = Number(values.rate_limit);
    }
    if (values.max_tickets) {
      request.max_tickets = Number(values.max_tickets);
    }
    if (values.transform_code) {
      request.transform_code = values.transform_code;
    }

    try {
      const job = await createJob.mutateAsync(
        values.export_csv ? toExportCsvRequest(request) : request,
      );
      toast.success(
        values.export_csv
          ? "CSV export job created"
          : values.dry_run
            ? "Dry-run import job created"
            : "Import job created",
      );
      form.reset();
      onSuccess(job.id);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to create import job";
      if (typeof err === "object" && err !== null && "response" in err) {
        const resp = (err as { response?: { data?: { detail?: unknown } } }).response;
        toast.error(apiErrorDetail(resp?.data?.detail) ?? message);
      } else {
        toast.error(message);
      }
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={(e) => { void form.handleSubmit(onSubmit)(e); }} className="space-y-6">
        {/* Source Type */}
        <FormField
          control={form.control}
          name="source"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Import Source</FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    // A CSV export reads from a provider; an uploaded file
                    // is already the tickets.
                    if (value === "file") form.setValue("export_csv", false);
                  }}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="provider" id="source-provider" />
                    <Label htmlFor="source-provider">ITSM Provider</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="file" id="source-file" />
                    <Label htmlFor="source-file">File Upload</Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {source === "provider" && (
          <FormField
            control={form.control}
            name="export_csv"
            render={({ field }) => (
              <FormItem className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <FormLabel>Export to CSV only</FormLabel>
                  <FormDescription>
                    Fetch the tickets and write them to one CSV file to
                    download. No tickets are created in Harmony
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        {/* Provider fields */}
        {source === "provider" && (
          <div className="space-y-4 rounded-md border p-4">
            {credentialsError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
                Failed to load integrations. Check that the tenant has ITSM credentials configured.
              </div>
            )}
            <FormField
              control={form.control}
              name="provider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Provider</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={credentialsLoading || credentialsError || availableProviders.length === 0}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            credentialsLoading
                              ? "Loading..."
                              : credentialsError
                                ? "Failed to load"
                                : availableProviders.length === 0
                                  ? "No ITSM integrations found"
                                  : "Select provider"
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {IMPORT_PROVIDERS
                        .filter((p) => availableProviders.includes(p.value))
                        .map((p) => (
                          <SelectItem key={p.value} value={p.value}>
                            {p.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="credential_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Credential</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={!selectedProvider || filteredCredentials.length === 0}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            !selectedProvider
                              ? "Select a provider first"
                              : filteredCredentials.length === 0
                                ? "No credentials found"
                                : "Select credential"
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {filteredCredentials.map((cred) => (
                        <SelectItem key={cred.id} value={cred.id}>
                          {cred.name}
                          {cred.label ? ` (${cred.label})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Integration credential for the selected provider
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="source_project"
              render={({ field }) => {
                const isFreshservice =
                  selectedProvider === "freshservice";
                // A monday import reads one board, and the importer refuses
                // a job that does not name it.
                const isMonday = selectedProvider === "monday";
                // SysAid scopes an import by assigned group ids.
                const isSysAid = selectedProvider === "sysaid";
                return (
                  <FormItem>
                    <FormLabel>
                      {isMonday
                        ? "Board ID"
                        : isSysAid
                          ? "Assigned Group IDs"
                          : isFreshservice
                            ? "Source Workspace"
                            : "Source Project"}{" "}
                      {isMonday ? "(required)" : "(optional)"}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={
                          isMonday
                            ? "e.g. 1234567890"
                            : isSysAid
                              ? "e.g. 3, 7 (empty imports every group)"
                              : isFreshservice
                              ? "e.g. workspace name"
                              : "e.g. JSM project key"
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="since_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Since Date (optional)</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="until_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Until Date (optional)</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {selectedProvider === "freshservice" && (
              <FormField
                control={form.control}
                name="record_types"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Record Types (optional)</FormLabel>
                    <Select
                      value={field.value === "" ? ALL_RECORD_TYPES_VALUE : field.value}
                      onValueChange={(v) => {
                        field.onChange(v === ALL_RECORD_TYPES_VALUE ? "" : v);
                      }}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {RECORD_TYPE_OPTIONS.map((o) => (
                          <SelectItem
                            key={o.value || ALL_RECORD_TYPES_VALUE}
                            value={o.value || ALL_RECORD_TYPES_VALUE}
                          >
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Which Freshservice records to import. Any brings both
                      tickets and changes. Choose Changes only for a
                      full-history backfill — an unbounded ticket sweep can
                      run for days and stall before reaching the changes.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {selectedProvider === "freshservice" && credentialId && (
              <FreshserviceGroupScope
                tenantId={tenantId}
                credentialId={credentialId}
              />
            )}

            <FormField
              control={form.control}
              name="include_closed"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <FormLabel>Include Closed Tickets</FormLabel>
                    <FormDescription>
                      Import tickets that are already closed/resolved
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {showsJiraImportOptions && (
              <FormField
                control={form.control}
                name="include_audit_log"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <FormLabel>Include Audit Log</FormLabel>
                      <FormDescription>
                        Replay each ticket&apos;s history (status, priority,
                        title and description changes) into the Harmony
                        ticket&apos;s activity timeline, at the original dates
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked);
                          if (!checked) {
                            form.setValue("include_custom_field_history", false);
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            {showsJiraImportOptions && includeAuditLog && (
              <FormField
                control={form.control}
                name="include_custom_field_history"
                render={({ field }) => (
                  <FormItem className="ml-6 flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <FormLabel>Include custom field history in description</FormLabel>
                      <FormDescription>
                        Append custom field changes (who, when, from and to)
                        to each ticket&apos;s description, oldest first. With
                        Custom Fields selected, covers only fields the
                        ticket&apos;s custom fields table can show; otherwise
                        covers all custom fields, including internal ones
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            {showsJiraImportOptions && (
              <FormField
                control={form.control}
                name="sync_linked_tickets"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-4 rounded-lg border p-3">
                    <div>
                      <FormLabel>Sync Linked Tickets</FormLabel>
                      <FormDescription>
                        Turn Jira issue links into Harmony ticket links, and
                        route each new ticket to the same desk as its
                        earliest-created linked ticket already in Harmony.
                        Tickets already in Harmony are not moved. Inherit uses
                        the integration&apos;s ITSM config setting
                      </FormDescription>
                    </div>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-[220px] shrink-0">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {LINKED_TICKETS_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                            {opt.value === "inherit" &&
                              inheritedLinkedTickets !== undefined &&
                              ` (${inheritedLinkedTickets ? "on" : "off"})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            )}

            {showsJiraImportOptions && credentialId && sourceProjectKey && (
              <FormField
                control={form.control}
                name="custom_field_mappings"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custom Fields</FormLabel>
                    <CustomFieldsSelector
                      tenantId={tenantId}
                      credentialId={credentialId}
                      projectKey={sourceProjectKey}
                      targetDeskId={createTickets ? targetDeskId : ""}
                      value={field.value}
                      onChange={field.onChange}
                      showRequiredErrors={form.formState.submitCount > 0}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        )}

        {/* File upload fields */}
        {source === "file" && (
          <div className="space-y-4 rounded-md border p-4">
            <FormField
              control={form.control}
              name="file_format"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>File Format</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select source format" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {FILE_FORMAT_PROVIDERS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Format of the uploaded file. Select the ITSM provider the data was exported from.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.watch("file_format") === "harmony" && <HarmonyFormatInfo />}

            <FormField
              control={form.control}
              name="s3_keys"
              render={() => (
                <FormItem>
                  <FormLabel>Upload Files</FormLabel>
                  <FormControl>
                    <FileUploadZone
                      tenantId={tenantId}
                      onUploadComplete={handleUploadComplete}
                      onUploadingChange={setIsUploading}
                      uploadedKeys={s3Keys}
                      onRemoveKey={handleRemoveKey}
                    />
                  </FormControl>
                  <FormDescription>
                    Supported formats: .jsonl, .jsonl.gz, .json, .zip
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Import Targets */}
        {!exportCsv && (
          <div className="space-y-4">
            <Label className="text-sm font-medium">Import Targets</Label>
            <div className="space-y-3 rounded-md border p-4">
              <FormField
                control={form.control}
                name="create_tickets"
                render={({ field }) => (
                  <FormItem className="flex items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Import to Service Desk</FormLabel>
                      <FormDescription>
                        Create tickets in a Service Desk desk
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {createTickets && (
                <FormField
                  control={form.control}
                  name="target_desk_id"
                  render={({ field }) => (
                    <FormItem className="ml-7">
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select target desk" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {desks?.map((desk) => (
                            <SelectItem key={desk.id} value={desk.id}>
                              {desk.emoji ? `${desk.emoji} ` : ""}
                              {desk.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {createTickets && targetDeskId && hasSubdesks && (
                <FormField
                  control={form.control}
                  name="target_subdesk_id"
                  render={({ field }) => (
                    <FormItem className="ml-7">
                      <Select
                        value={field.value || AUTO_SUBDESK_VALUE}
                        onValueChange={(value) => {
                          field.onChange(
                            value === AUTO_SUBDESK_VALUE ? "" : value,
                          );
                        }}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select target sub-desk" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={AUTO_SUBDESK_VALUE}>
                            Automatic (route by desk settings)
                          </SelectItem>
                          {subdesks?.map((subdesk) => (
                            <SelectItem key={subdesk.id} value={subdesk.id}>
                              {subdesk.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        All imported tickets will be routed to this sub-desk
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="generate_insights"
                render={({ field }) => (
                  <FormItem className="flex items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Generate KB Insights</FormLabel>
                      <FormDescription>
                        Feed resolved tickets to the insights pipeline for classification
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </div>
          </div>
        )}

        {/* Advanced Options */}
        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="flex items-center gap-1 text-sm"
            >
              Advanced Options
              <ChevronDown
                className={`h-4 w-4 transition-transform ${advancedOpen ? "rotate-180" : ""}`}
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-2">
            {!exportCsv && (
              <FormField
                control={form.control}
                name="dry_run"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div>
                      <FormLabel>Dry Run</FormLabel>
                      <FormDescription>
                        Simulate the import without creating tickets
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="skip_dedup"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <FormLabel>Skip Deduplication</FormLabel>
                    <FormDescription>
                      Import even if tickets were previously imported
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={selectedProvider === "monday"}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="rate_limit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rate Limit (optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="requests/sec"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="max_tickets"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Tickets (optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="limit"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {!exportCsv && (
              <FormField
                control={form.control}
                name="transform_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Transform Code (optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Python code to transform ticket fields..."
                        className="font-mono text-sm"
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Custom Python code executed per ticket to modify fields
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Submit */}
        <Button type="submit" disabled={createJob.isPending || isUploading} className="w-full">
          {(createJob.isPending || isUploading) && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {isUploading
            ? "Uploading files..."
            : exportCsv
              ? "Start Export"
              : form.watch("dry_run")
                ? "Start Dry Run"
                : "Start Import"}
        </Button>
      </form>
    </Form>
  );
}
